import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';

const FounderMessage: React.FC = () => {
  const [activeQuote, setActiveQuote] = useState(0);
  const containerRef = useRef(null);
  const messageRef = useRef(null);
  const imageRef = useRef(null);
  const isMessageInView = useInView(messageRef, { once: true, amount: 0.3 });
  const isImageInView = useInView(imageRef, { once: true, amount: 0.3 });
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.4, 0.8], [1, 1, 0.8, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8], [1, 1, 0.9]);
  const y = useTransform(scrollYProgress, [0, 0.8], [0, 100]);
  
  // Inspirational quotes from the founder
  const quotes = [
    "Education is the bridge that connects dreams to reality.",
    "Every student deserves a guide for their journey abroad.",
    "Cultural exchange enriches both the student and society.",
    "The path to global success begins with a single step abroad."
  ];
  
  // Auto-cycle through quotes
  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveQuote(prev => (prev + 1) % quotes.length);
    }, 5000);
    
    return () => clearInterval(intervalId);
  }, [quotes.length]);

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen overflow-hidden">
      {/* Advanced hero section with dynamic 3D parallax effect */}
      <motion.div
        ref={containerRef}
        className="relative h-[80vh] md:h-[90vh] overflow-hidden flex items-center justify-center perspective-1000"
        style={{ opacity, scale, y }}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Dynamic gradient background with animation */}
          <motion.div 
            className="absolute -inset-[100px] bg-gradient-to-br from-primary/10 via-white to-secondary/10"
            animate={{ 
              background: [
                "linear-gradient(to bottom right, rgba(227, 6, 19, 0.1), #ffffff, rgba(0, 82, 164, 0.1))",
                "linear-gradient(to bottom right, rgba(0, 82, 164, 0.1), #ffffff, rgba(227, 6, 19, 0.1))",
                "linear-gradient(to bottom right, rgba(227, 6, 19, 0.1), rgba(0, 82, 164, 0.1), #ffffff)"
              ]
            }}
            transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
          />
          
          {/* Animated geometric patterns */}
          <div className="absolute inset-0 opacity-20">
            <motion.div
              className="absolute w-full h-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 150, repeat: Infinity, ease: "linear" }}
            >
              <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(227, 6, 19, 0.2)" strokeWidth="0.5"/>
                </pattern>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </motion.div>
          </div>
          
          {/* Advanced animated particles with trails */}
          <div className="absolute inset-0 overflow-hidden">
            {Array.from({ length: 25 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  width: 1 + Math.random() * 4,
                  height: 1 + Math.random() * 4,
                  filter: "blur(1px)",
                  boxShadow: [
                    "0 0 10px 2px rgba(227, 6, 19, 0.3)",
                    "0 0 8px 2px rgba(0, 82, 164, 0.3)",
                    "0 0 12px 2px rgba(227, 6, 19, 0.3)",
                  ][i % 3],
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  zIndex: 1,
                }}
                animate={{
                  y: [0, Math.random() * 700 - 350],
                  x: [0, Math.random() * 700 - 350],
                  opacity: [0, 0.8, 0],
                  scale: [0, 1, 0],
                }}
                transition={{
                  duration: 10 + Math.random() * 20,
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
          
          {/* Animated abstract shapes */}
          <div className="absolute inset-0 overflow-hidden">
            {Array.from({ length: 3 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full opacity-10"
                style={{
                  width: 300 + Math.random() * 500,
                  height: 300 + Math.random() * 500,
                  background: [
                    "radial-gradient(circle, rgba(227, 6, 19, 0.5) 0%, rgba(227, 6, 19, 0) 70%)",
                    "radial-gradient(circle, rgba(0, 82, 164, 0.5) 0%, rgba(0, 82, 164, 0) 70%)",
                    "radial-gradient(circle, rgba(227, 6, 19, 0.5) 0%, rgba(227, 6, 19, 0) 70%)"
                  ][i % 3],
                  left: `${20 + Math.random() * 60}%`,
                  top: `${20 + Math.random() * 60}%`,
                  transformOrigin: "center",
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  x: [0, Math.random() * 100 - 50],
                  y: [0, Math.random() * 100 - 50],
                  rotate: [0, Math.random() * 60 - 30],
                }}
                transition={{
                  duration: 20 + Math.random() * 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        </div>
        
        {/* Cinematic 3D content with depth effect */}
        <div className="container mx-auto px-4 md:px-6 relative z-10 perspective-1000">
          {/* 3D rotating title with hover effects */}
          <motion.h1 
            className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-8 text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 1.2, 
              type: "spring",
              stiffness: 50
            }}
            style={{ 
              textShadow: "0 10px 30px rgba(227, 6, 19, 0.2)"
            }}
          >
            <motion.span 
              className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-primary"
              whileHover={{ 
                scale: 1.05,
                rotateY: 15,
                textShadow: "0 20px 40px rgba(227, 6, 19, 0.4)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              From the Founder
            </motion.span>
          </motion.h1>
          
          {/* Animated quote carousel with 3D perspective */}
          <motion.div 
            className="relative h-24 md:h-32 mb-12 overflow-hidden perspective-1000 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeQuote}
                className="absolute inset-0 flex items-center justify-center"
                initial={{ 
                  opacity: 0, 
                  rotateX: 45,
                  y: 50,
                  scale: 0.8,
                  z: -100
                }}
                animate={{ 
                  opacity: 1, 
                  rotateX: 0,
                  y: 0,
                  scale: 1,
                  z: 0
                }}
                exit={{ 
                  opacity: 0, 
                  rotateX: -45,
                  y: -50,
                  scale: 0.8,
                  z: -100
                }}
                transition={{ 
                  duration: 0.8,
                  type: "spring",
                  stiffness: 70,
                  damping: 20
                }}
              >
                <blockquote className="text-xl md:text-3xl font-medium text-center">
                  <span className="text-primary text-5xl absolute -left-8 top-0">"</span>
                  <p className="text-gray-700">{quotes[activeQuote]}</p>
                  <span className="text-primary text-5xl absolute -bottom-8 right-0">"</span>
                </blockquote>
              </motion.div>
            </AnimatePresence>
            
            {/* Glowing line beneath quote */}
            <motion.div
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "60%", opacity: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
              style={{
                boxShadow: "0 0 10px 1px rgba(227, 6, 19, 0.5)"
              }}
            />
          </motion.div>
          
          {/* Indicator dots with hover effect */}
          <motion.div
            className="flex justify-center space-x-3 mb-12 perspective-1000"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            {quotes.map((_, index) => (
              <motion.button
                key={index}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeQuote 
                    ? "bg-gradient-to-r from-primary to-secondary shadow-lg" 
                    : "bg-primary/20"
                }`}
                whileHover={{ 
                  scale: 1.5,
                  y: -2,
                  boxShadow: "0 5px 10px rgba(227, 6, 19, 0.5)"
                }}
                onClick={() => setActiveQuote(index)}
                style={{ 
                  boxShadow: index === activeQuote 
                    ? "0 0 10px rgba(227, 6, 19, 0.6)" 
                    : "none",
                  transformStyle: "preserve-3d"
                }}
              />
            ))}
          </motion.div>
          
          {/* Animated scroll indicator */}
          <motion.div
            className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-primary perspective-1000"
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: [0, 1, 0], 
              y: [0, 10, 0],
              rotateX: [0, 20, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          >
            <div className="relative flex flex-col items-center">
              <span className="text-sm font-medium mb-2 text-primary">Discover My Story</span>
              <motion.svg 
                className="w-8 h-8" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
                animate={{
                  y: [0, 6, 0]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                />
              </motion.svg>
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Main content with advanced scrolling effects */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 py-20 relative">
        {/* Subtle navigation breadcrumbs with hover effect */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center text-sm text-gray-500">
            <a href="/" className="hover:text-primary transition-colors">Home</a>
            <svg className="mx-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <a href="/about" className="hover:text-primary transition-colors">About</a>
            <svg className="mx-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-primary font-medium">Founder's Message</span>
          </div>
        </motion.div>

        {/* Advanced content grid with parallax scrolling */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
          {/* Founder's image with floating effect */}
          <motion.div 
            ref={imageRef}
            className="lg:col-span-5 order-2 lg:order-1 relative perspective-1000"
            initial={{ opacity: 0, x: -50 }}
            animate={isImageInView ? { opacity: 1, x: 0 } : {}}
            transition={{ 
              duration: 0.8, 
              type: "spring",
              stiffness: 50
            }}
          >
            <div className="relative rounded-lg overflow-hidden">
              {/* Animated gradient border */}
              <motion.div 
                className="absolute -inset-1 rounded-lg z-0"
                style={{ 
                  background: "linear-gradient(45deg, #e30613, #0052A4, #e30613)",
                  filter: "blur(20px)",
                  opacity: 0.5
                }}
                animate={{
                  background: [
                    "linear-gradient(45deg, #e30613, #0052A4, #e30613)",
                    "linear-gradient(45deg, #0052A4, #e30613, #0052A4)",
                    "linear-gradient(45deg, #e30613, #0052A4, #e30613)"
                  ]
                }}
                transition={{ duration: 10, repeat: Infinity }}
              />
              
              {/* Image with 3D hover effect */}
              <motion.div 
                className="relative z-10 rounded-lg overflow-hidden shadow-2xl"
                whileHover={{ 
                  scale: 1.02,
                  rotateY: 5,
                  rotateX: 5,
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)"
                }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <img 
                  src="../../../images/team/director.jpg" 
                  alt="Durga Bahadur Karki - Founder of Recall Nepal"
                  className="w-full h-full object-cover transform"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                
                {/* Caption with subtle animation */}
                <motion.div 
                  className="absolute bottom-0 left-0 right-0 p-6 text-white"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                >
                  <h3 className="text-xl font-bold mb-1 text-white">Durga Bahadur Karki</h3>
                  <p className="text-sm text-gray-200">Founder & CEO, Recall Nepal</p>
                </motion.div>
              </motion.div>
            </div>
            
            {/* Floating 3D decorative elements */}
            <div className="absolute -z-10">
              {Array.from({ length: 3 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full bg-gradient-to-br"
                  style={{
                    width: 60 + i * 30,
                    height: 60 + i * 30,
                    background: [
                      "linear-gradient(45deg, rgba(227, 6, 19, 0.2), rgba(227, 6, 19, 0))",
                      "linear-gradient(45deg, rgba(0, 82, 164, 0.2), rgba(0, 82, 164, 0))",
                      "linear-gradient(45deg, rgba(227, 6, 19, 0.2), rgba(227, 6, 19, 0))"
                    ][i % 3],
                    left: `${-20 + i * 20}%`,
                    top: `${-20 + i * 10}%`,
                    transformStyle: "preserve-3d",
                    zIndex: -1,
                  }}
                  animate={{
                    x: [0, 10, 0, -10, 0],
                    y: [0, 15, 0, -15, 0],
                    rotateZ: [0, 10, 0, -10, 0],
                    scale: [1, 1.05, 1, 0.95, 1]
                  }}
                  transition={{
                    duration: 10 + i * 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 1.5
                  }}
                />
              ))}
            </div>
            
            {/* Experience tag with glowing effect */}
            <motion.div 
              className="absolute top-6 -right-4 z-20 bg-gradient-to-r from-primary to-secondary px-4 py-3 rounded-lg shadow-xl"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 0 20px rgba(227, 6, 19, 0.6)"
              }}
              style={{
                boxShadow: "0 5px 15px rgba(227, 6, 19, 0.4)"
              }}
            >
              <div className="text-white font-bold">
                <span className="block text-sm">8+ Years</span>
                <span className="block text-xs opacity-80">of Excellence</span>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Message content with advanced scroll animations */}
          <motion.div 
            ref={messageRef}
            className="lg:col-span-7 order-1 lg:order-2 relative"
            initial={{ opacity: 0, x: 50 }}
            animate={isMessageInView ? { opacity: 1, x: 0 } : {}}
            transition={{ 
              duration: 0.8, 
              type: "spring",
              stiffness: 50
            }}
          >
            <div className="space-y-8">
              {/* Section with staggered text animations */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={isMessageInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.3 }}
              >
                <motion.h2 
                  className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary"
                  initial={{ y: 20 }}
                  animate={isMessageInView ? { y: 0 } : {}}
                  transition={{ delay: 0.3 }}
                >
                  Our Vision & Journey
                </motion.h2>
                
                <div className="space-y-6">
                  {[
                    "Namaste and warm greetings. As the founder of Recall Nepal, I am deeply honored to share our journey with you. What began as a simple vision to connect Nepali students with international opportunities has blossomed into a comprehensive educational consultancy that has transformed thousands of lives.",
                    "In 2017, I recognized a critical gap in our educational landscape. Talented students with dreams of international education were struggling without proper guidance. Having experienced these challenges firsthand during my own educational journey, I was determined to create a solution that would empower our youth.",
                    "Today, Recall Nepal stands as a testament to that commitment. We have built strong partnerships with prestigious universities across Japan, creating pathways that were once unimaginable for many Nepali students."
                  ].map((paragraph, index) => (
                    <motion.p 
                      key={index}
                      className="text-gray-700 leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isMessageInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ 
                        delay: 0.4 + (index * 0.2),
                        duration: 0.8 
                      }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              </motion.div>
              
              {/* Animated divider with glow effect */}
              <motion.div 
                className="w-full h-px my-8"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={isMessageInView ? { scaleX: 1, opacity: 1 } : {}}
                transition={{ delay: 1.2, duration: 0.8 }}
                style={{ 
                  background: "linear-gradient(to right, transparent, rgba(227, 6, 19, 0.5), transparent)",
                  boxShadow: "0 0 8px rgba(227, 6, 19, 0.3)",
                  transformOrigin: "left"
                }}
              />
              
              {/* Core values with interactive elements */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isMessageInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1.4, duration: 0.8 }}
              >
                <h3 className="text-2xl font-bold mb-6 text-gray-800">Our Core Values</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      title: "Student Success",
                      description: "We measure our success by the achievements of our students.",
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      title: "Cultural Integration",
                      description: "We prepare students not just academically but culturally.",
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      title: "Ethical Guidance",
                      description: "We prioritize honesty, transparency, and ethical practices.",
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )
                    },
                    {
                      title: "Lifelong Support",
                      description: "Our relationship extends beyond university admission.",
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      )
                    }
                  ].map((value, index) => (
                    <motion.div
                      key={index}
                      className="bg-white rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl border border-gray-100"
                      whileHover={{ 
                        y: -5,
                        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                        background: "linear-gradient(to bottom right, white, #faf5ff)"
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isMessageInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ 
                        delay: 1.6 + (index * 0.15),
                        duration: 0.6 
                      }}
                    >
                      <div className="text-primary mb-4 flex items-center">
                        <motion.span 
                          className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10"
                          whileHover={{ 
                            rotate: 10,
                            scale: 1.1,
                            backgroundColor: "#e30613",
                            color: "white"
                          }}
                        >
                          {value.icon}
                        </motion.span>
                      </div>
                      <h4 className="text-lg font-semibold mb-2 text-gray-800">{value.title}</h4>
                      <p className="text-sm text-gray-600">{value.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              
              {/* Personal note with signature animation */}
              <motion.div
                className="mt-10 relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isMessageInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 2, duration: 0.8 }}
              >
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-6 md:p-8 rounded-xl relative overflow-hidden">
                  {/* Background decorative elements */}
                  <div className="absolute -right-20 -bottom-20 text-primary opacity-20">
                    <svg width="200" height="200" viewBox="0 0 100 100" fill="currentColor">
                      <path d="M95,50c0,24.9-20.1,45-45,45S5,74.9,5,50S25.1,5,50,5S95,25.1,95,50z M70,35c0-11-9-20-20-20s-20,9-20,20s9,20,20,20 S70,46,70,35z"/>
                    </svg>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-primary">A Personal Note</h3>
                  <p className="text-gray-700 mb-4 relative z-10">
                    "Education is not just about acquiring degrees; it's about expanding horizons, embracing diversity, and developing a global perspective. At Recall Nepal, we are committed to nurturing not just successful students, but compassionate global citizens who will represent our nation with pride and contribute meaningfully to society."
                  </p>
                  
                  {/* Animated signature */}
                  <motion.div 
                    className="flex items-center mt-6"
                    initial={{ opacity: 0, x: -20 }}
                    animate={isMessageInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 2.2 }}
                  >
                    <motion.svg 
                      width="100" 
                      height="50" 
                      viewBox="0 0 100 50" 
                      fill="none" 
                      className="text-primary"
                      initial={{ pathLength: 0 }}
                      animate={isMessageInView ? { pathLength: 1 } : {}}
                      transition={{ delay: 2.3, duration: 1.5 }}
                    >
                      <motion.path 
                        d="M10,25 C20,10 30,40 40,25 C50,10 60,40 90,25" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={isMessageInView ? { pathLength: 1 } : {}}
                        transition={{ delay: 2.3, duration: 1.5 }}
                      />
                    </motion.svg>
                    <div className="ml-4">
                      <p className="font-semibold text-gray-800">Durga Bahadur Karki</p>
                      <p className="text-sm text-gray-600">Founder & CEO</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Testimonial/Impact section */}
      <div className="bg-gradient-to-r from-primary to-secondary py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="max-w-3xl mx-auto text-center text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Our Impact</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                { count: "2,000+", label: "Students Placed" },
                { count: "30+", label: "University Partners" },
                { count: "90%", label: "Success Rate" },
                { count: "8+", label: "Years of Excellence" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  <div className="text-3xl md:text-4xl font-bold mb-2">{stat.count}</div>
                  <div className="text-white/80">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            <motion.p
              className="text-xl leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              "Our mission extends beyond just placement. We're building a community of global citizens
              who bring their knowledge back to Nepal, creating a cycle of growth and opportunity."
            </motion.p>
          </motion.div>
        </div>
      </div>
      
      {/* Call to action */}
      <div className="py-20 md:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Start Your Journey Today</h2>
            <p className="text-xl text-gray-700 mb-10">
              Join thousands of successful students who have transformed their lives through 
              education in Japan. Let us help you take the first step.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact"
                className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-full shadow-xl hover:shadow-primary/30 transition-all"
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 15px 30px rgba(227, 6, 19, 0.4)",
                  y: -5
                }}
                whileTap={{ scale: 0.98 }}
              >
                Book a Consultation
              </motion.a>
              
              <motion.a
                href="/services"
                className="px-8 py-4 bg-white text-primary font-bold rounded-full shadow-xl border-2 border-primary hover:shadow-primary/20 transition-all"
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 15px 30px rgba(227, 6, 19, 0.2)",
                  y: -5
                }}
                whileTap={{ scale: 0.98 }}
              >
                Explore Our Services
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FounderMessage; 